import * as models from 'smg/admin/models';
import { Session } from 'smg/admin/session';
import axios from 'smg/axios';
import { IsAxiosError, ShowError } from 'smg/errors';
import QueryString from 'smg/querystring';
import * as cartID from '../cart/cartid';
import { AddCartItems } from '../cart/cartitems';
import { ApplyAddParamsFromQuery, SwitchCart } from '../cart/cartactions';

// GetCheckoutCart loads the session cart for the checkout page. A `cartid`
// query param deep-links into checkout for a specific cart (see
// applyCartIDParam). On 404 it forgets the stored cartID (matches the shared
// cart fetcher) and resolves to undefined so the page can render its empty
// state without throwing.
export async function GetCheckoutCart(): Promise<models.CartPreviewResponse | undefined> {
    await applyCartIDParam();
    // Deep-link adds on checkout start a fresh cart, then add — mirroring the
    // customer-site checkout (express/ts/pages/checkout.ts newCart:true).
    await ApplyAddParamsFromQuery(true);

    const id = cartID.Get();
    if (!id) {
        return undefined;
    }
    return axios.get(`/v2/carts/${id}`, { params: { data: 'id,name,active,ordered,user,cartItems' } }).
        then(r => {
            const resp = models.CartPreviewResponse.fromJSON(r.data);
            // The read returns the cart by id, including a saved or ordered one,
            // so the page drops those here — matches pages/cart.ts getCart.
            if (resp.cart.active !== true || resp.cart.ordered === true) {
                void cartID.Forget();
                return undefined;
            }
            return resp;
        }).
        catch(err => {
            if (IsAxiosError(err) && err.response?.status === 404) {
                void cartID.Forget();
                return undefined;
            }
            ShowError(err);
            throw err;
        });
}

// applyCartIDParam consumes a `cartid` query param — a deep-link into checkout
// for a specific cart — and makes that cart the active one, mirroring the
// carts page "Set as Cart" action (iap/v3/ts/pages/carts.ts): switch to it if
// the current user owns it, otherwise duplicate it into a cart they own (a
// user can only have their own cart active). The param is consumed and
// stripped from the URL — matching the loginToken/CustomerEmailID pattern in
// main.ts — so a refresh or a watcher-triggered reload loads the resulting
// cart from storage without re-applying the deep-link.
async function applyCartIDParam(): Promise<void> {
    const queryCartID = QueryString().get('cartid');
    if (!queryCartID) {
        return;
    }
    QueryString().delete('cartid');

    const oldCartID = cartID.Get();
    if (queryCartID === oldCartID) {
        return;
    }

    // Probe the target to learn its owner. A plain fetch (not GetCheckoutCart)
    // so an invalid/inaccessible id can't Forget() the user's existing cart.
    const sess = await Session();
    const target = await axios.get(`/v2/carts/${queryCartID}`, { params: { data: 'id,user' } }).
        then(r => {
            const data = models.CartPreviewResponse.fromJSON(r.data);
            return data.cart;
        }).
        catch(() => undefined);
    if (!target?.id) {
        return;
    }

    if (target.user?.id === sess.user.id) {
        await SwitchCart(queryCartID, oldCartID, false);
    } else {
        // Don't own it — duplicate into a new cart we own (the "Created
        // duplicate cart" path), which AddCartItems sets as active.
        await AddCartItems({ cartID: queryCartID }, true);
    }
}
